'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Workshops', href: '/workshop-listings' },
  { label: 'Law Hub', href: '/law-resource-hub' },
  { label: 'Community', href: '/community-space' },
];

const ADMIN_EMAIL = 'adithyavishwanath2010@gmail.com';

function ScalesLogo({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Shield background */}
      <path d="M24 3L6 10V24C6 33.5 14 41.5 24 45C34 41.5 42 33.5 42 24V10L24 3Z" fill="#1B2A4A"/>
      {/* Shield inner highlight */}
      <path d="M24 7L10 13V24C10 31.8 16.5 38.5 24 41.5C31.5 38.5 38 31.8 38 24V13L24 7Z" fill="#243660"/>
      {/* Scales pillar */}
      <rect x="23" y="14" width="2" height="16" rx="1" fill="#C9A84C"/>
      {/* Scales base */}
      <rect x="18" y="29" width="12" height="2" rx="1" fill="#C9A84C"/>
      {/* Scales crossbar */}
      <rect x="13" y="15" width="22" height="1.5" rx="0.75" fill="#C9A84C"/>
      {/* Left chain */}
      <line x1="15" y1="16.5" x2="15" y2="22" stroke="#C9A84C" strokeWidth="1" strokeLinecap="round"/>
      {/* Right chain */}
      <line x1="33" y1="16.5" x2="33" y2="22" stroke="#C9A84C" strokeWidth="1" strokeLinecap="round"/>
      {/* Left pan */}
      <path d="M11 22 Q15 26 19 22" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      {/* Right pan */}
      <path d="M29 22 Q33 26 37 22" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      {/* Top gem */}
      <circle cx="24" cy="14" r="1.8" fill="#C9A84C"/>
      {/* Olive branch left */}
      <path d="M14 33 Q16 31 18 33" stroke="#C9A84C" strokeWidth="1" strokeLinecap="round" fill="none" opacity="0.7"/>
      {/* Olive branch right */}
      <path d="M30 33 Q32 31 34 33" stroke="#C9A84C" strokeWidth="1" strokeLinecap="round" fill="none" opacity="0.7"/>
    </svg>
  );
}

export default function Navbar() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [isApprovedTeacher, setIsApprovedTeacher] = useState(false);

  const isAdmin = user?.email === ADMIN_EMAIL || user?.user_metadata?.role === 'admin';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    if (!user || isAdmin) {
      setIsApprovedTeacher(isAdmin);
      return;
    }
    const supabase = createClient();
    supabase
      .from('role_applications')
      .select('status')
      .eq('user_id', user.id)
      .eq('role', 'teacher')
      .eq('status', 'approved')
      .maybeSingle()
      .then(({ data }) => setIsApprovedTeacher(!!data));
  }, [user, isAdmin]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      router?.push('/');
      router?.refresh();
    } finally {
      setSigningOut(false);
    }
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Member';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/97 backdrop-blur-md shadow-nav border-b border-border'
            : 'bg-white border-b border-border'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3 group">
              <ScalesLogo size={34} />
              <div className="flex flex-col leading-none">
                <span className="font-bold text-base tracking-[0.08em] text-primary group-hover:opacity-85 transition-opacity uppercase">
                  Project Concord
                </span>
                <span className="text-[9px] tracking-[0.18em] text-accent font-medium uppercase">Justice · Unity · Voice</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-0.5">
              {navLinks?.map((link) => {
                const isActive = pathname === link?.href;
                return (
                  <Link
                    key={`nav-${link?.href}`}
                    href={link?.href}
                    className={`px-4 py-2 rounded text-sm font-medium tracking-wide transition-all duration-150 ${
                      isActive
                        ? 'text-primary font-semibold border-b-2 border-accent rounded-none pb-1.5' :'text-zinc-500 hover:text-primary hover:bg-secondary'
                    }`}
                  >
                    {link?.label}
                  </Link>
                );
              })}
              {user && (
                <Link
                  href="/dashboard"
                  className={`px-4 py-2 rounded text-sm font-medium tracking-wide transition-all duration-150 ${
                    pathname === '/dashboard' ?'text-primary font-semibold border-b-2 border-accent rounded-none pb-1.5' :'text-zinc-500 hover:text-primary hover:bg-secondary'
                  }`}
                >
                  Dashboard
                </Link>
              )}
              {user && isApprovedTeacher && !isAdmin && (
                <Link
                  href="/teacher"
                  className={`px-4 py-2 rounded text-sm font-medium tracking-wide transition-all duration-150 flex items-center gap-1.5 ${
                    pathname === '/teacher' ?'text-primary font-semibold border-b-2 border-accent rounded-none pb-1.5' :'text-zinc-500 hover:text-primary hover:bg-secondary'
                  }`}
                >
                  <Icon name="AcademicCapIcon" size={14} variant="outline" />
                  Teacher Portal
                </Link>
              )}
              {user && isAdmin && (
                <Link
                  href="/teacher"
                  className={`px-4 py-2 rounded text-sm font-medium tracking-wide transition-all duration-150 flex items-center gap-1.5 ${
                    pathname === '/teacher' ?'text-primary font-semibold border-b-2 border-accent rounded-none pb-1.5' :'text-zinc-500 hover:text-primary hover:bg-secondary'
                  }`}
                >
                  <Icon name="AcademicCapIcon" size={14} variant="outline" />
                  Teacher Portal
                </Link>
              )}
              {user && isAdmin && (
                <Link
                  href="/admin/registrations"
                  className={`px-4 py-2 rounded text-sm font-medium tracking-wide transition-all duration-150 flex items-center gap-1.5 ${
                    pathname === '/admin/registrations' ?'text-primary font-semibold border-b-2 border-accent rounded-none pb-1.5' :'text-zinc-500 hover:text-primary hover:bg-secondary'
                  }`}
                >
                  <Icon name="ClipboardDocumentListIcon" size={14} variant="outline" />
                  Registrations
                </Link>
              )}
              {user && isAdmin && (
                <Link
                  href="/admin/blog-submissions"
                  className={`px-4 py-2 rounded text-sm font-medium tracking-wide transition-all duration-150 flex items-center gap-1.5 ${
                    pathname === '/admin/blog-submissions' ?'text-primary font-semibold border-b-2 border-accent rounded-none pb-1.5' :'text-zinc-500 hover:text-primary hover:bg-secondary'
                  }`}
                >
                  <Icon name="DocumentTextIcon" size={14} variant="outline" />
                  Blog Submissions
                </Link>
              )}
              {user && isAdmin && (
                <Link
                  href="/admin/applications"
                  className={`px-4 py-2 rounded text-sm font-medium tracking-wide transition-all duration-150 flex items-center gap-1.5 ${
                    pathname === '/admin/applications' ?'text-primary font-semibold border-b-2 border-accent rounded-none pb-1.5' :'text-zinc-500 hover:text-primary hover:bg-secondary'
                  }`}
                >
                  <Icon name="UserGroupIcon" size={14} variant="outline" />
                  Applications
                </Link>
              )}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              {!loading && (
                user ? (
                  <div className="flex items-center gap-3">
                    <Link
                      href="/dashboard"
                      className="flex items-center gap-2 px-3 py-1.5 rounded bg-secondary border border-border hover:bg-muted transition-colors"
                    >
                      <div className="w-6 h-6 rounded-full crimson-gradient flex items-center justify-center text-white text-xs font-bold">
                        {displayName?.[0]?.toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-foreground max-w-[120px] truncate">{displayName}</span>
                    </Link>
                    <button
                      onClick={handleSignOut}
                      disabled={signingOut}
                      className="btn-ghost text-sm px-4 py-2"
                    >
                      {signingOut ? (
                        <Icon name="ArrowPathIcon" size={14} variant="outline" className="animate-spin" />
                      ) : (
                        'Sign Out'
                      )}
                    </button>
                  </div>
                ) : (
                  <>
                    <Link href="/login" className="btn-ghost text-sm px-4 py-2 text-primary">
                      Sign In
                    </Link>
                    <Link href="/login" className="btn-primary text-sm px-5 py-2.5">
                      Join Us
                    </Link>
                  </>
                )
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              className="md:hidden p-2 rounded text-zinc-600 hover:bg-secondary transition-colors"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation menu"
            >
              <Icon name="Bars3Icon" size={22} variant="outline" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-all duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        <div
          className={`absolute top-0 right-0 h-full w-72 bg-white shadow-2xl transition-transform duration-300 ease-out ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div className="flex items-center gap-2.5">
              <ScalesLogo size={28} />
              <div className="flex flex-col leading-none">
                <span className="font-bold text-sm tracking-[0.12em] text-primary uppercase">CRIMSON</span>
                <span className="text-[8px] tracking-[0.16em] text-accent font-medium uppercase">Law &amp; Advocacy</span>
              </div>
            </div>
            <button
              className="p-2 rounded text-zinc-600 hover:bg-secondary transition-colors"
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation menu"
            >
              <Icon name="XMarkIcon" size={20} variant="outline" />
            </button>
          </div>
          <nav className="p-4 flex flex-col gap-1">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={`mobile-nav-${link?.href}`}
                  href={link?.href}
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded text-sm font-medium tracking-wide transition-all duration-150 ${
                    isActive
                      ? 'bg-primary text-white' :'text-zinc-600 hover:text-primary hover:bg-secondary'
                  }`}
                >
                  {link?.label}
                </Link>
              );
            })}
            {user && (
              <Link
                href="/dashboard"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded text-sm font-medium tracking-wide transition-all duration-150 ${
                  pathname === '/dashboard' ?'bg-primary text-white' :'text-zinc-600 hover:text-primary hover:bg-secondary'
                }`}
              >
                Dashboard
              </Link>
            )}
            {user && isApprovedTeacher && (
              <Link
                href="/teacher"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded text-sm font-medium tracking-wide transition-all duration-150 flex items-center gap-2 ${
                  pathname === '/teacher' ?'bg-primary text-white' :'text-zinc-600 hover:text-primary hover:bg-secondary'
                }`}
              >
                <Icon name="AcademicCapIcon" size={16} variant="outline" />
                Teacher Portal
              </Link>
            )}
            {user && isAdmin && (
              <Link
                href="/admin/registrations"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded text-sm font-medium tracking-wide transition-all duration-150 flex items-center gap-2 ${
                  pathname === '/admin/registrations' ?'bg-primary text-white' :'text-zinc-600 hover:text-primary hover:bg-secondary'
                }`}
              >
                <Icon name="ClipboardDocumentListIcon" size={16} variant="outline" />
                Registrations
              </Link>
            )}
            {user && isAdmin && (
              <Link
                href="/admin/blog-submissions"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded text-sm font-medium tracking-wide transition-all duration-150 flex items-center gap-2 ${
                  pathname === '/admin/blog-submissions' ?'bg-primary text-white' :'text-zinc-600 hover:text-primary hover:bg-secondary'
                }`}
              >
                <Icon name="DocumentTextIcon" size={16} variant="outline" />
                Blog Submissions
              </Link>
            )}
            {user && isAdmin && (
              <Link
                href="/admin/applications"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded text-sm font-medium tracking-wide transition-all duration-150 flex items-center gap-2 ${
                  pathname === '/admin/applications' ?'bg-primary text-white' :'text-zinc-600 hover:text-primary hover:bg-secondary'
                }`}
              >
                <Icon name="UserGroupIcon" size={16} variant="outline" />
                Applications
              </Link>
            )}
            <div className="mt-4 pt-4 border-t border-border flex flex-col gap-2">
              {!loading && (
                user ? (
                  <>
                    <Link
                      href="/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded bg-secondary hover:bg-muted transition-colors"
                    >
                      <div className="w-7 h-7 rounded-full crimson-gradient flex items-center justify-center text-white text-xs font-bold">
                        {displayName?.[0]?.toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-foreground truncate">{displayName}</span>
                    </Link>
                    <button
                      onClick={() => { setMobileOpen(false); handleSignOut(); }}
                      className="btn-ghost w-full justify-center text-sm"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link href="/login" onClick={() => setMobileOpen(false)} className="btn-ghost w-full justify-center text-sm text-primary">
                      Sign In
                    </Link>
                    <Link href="/login" onClick={() => setMobileOpen(false)} className="btn-primary w-full justify-center">
                      Join the Movement
                    </Link>
                  </>
                )
              )}
            </div>
          </nav>
          <div className="absolute bottom-8 left-0 right-0 px-6">
            <p className="text-xs text-muted-foreground text-center tracking-wide">Empowering youth advocates globally</p>
          </div>
        </div>
      </div>
    </>
  );
}